/* 186 B.7a [RULED 186 (10)] — THE PAGER, EXTRACTED ONCE: the page state and the
 * arithmetic the chronology (CaseTimelineSection) and the Documents section
 * (CaseDocumentsSection) each carried as their own copy — `PG = 10` beside
 * `pageSize = 10`, `tot` / `cur` / the slice twice over, byte for byte the same
 * sums. ONE mechanism now, available across the app; the Notes section is its
 * third caller (B.7b), not a third copy. The `.csPgr` control that goes with
 * it is `components/Pager.tsx` — the markup moved there, the class stays in
 * App.css.
 *
 * TWO PARTS, because both copies hold their `page` state at the top of the
 * component (rules of hooks: before the early returns) and compute their rows
 * hundreds of lines later — so the STATE is a hook and the ARITHMETIC is a
 * pure function a caller applies once its rows exist. Neither section was
 * restructured to take a one-call shape.
 *
 * EXTRACT, DON'T IMPROVE: the three expressions are the copies' own —
 *   tot   Math.max(1, Math.ceil(items.length / pageSize))
 *   cur   Math.min(page, tot - 1)      (a page past the end clamps to the last)
 *   slice items.slice(cur * pageSize, (cur + 1) * pageSize)
 * — and the page size is the app's answer, TEN, the number both sections
 * already agreed on. A caller that names its own page size keeps it (the
 * Documents section's `pageSize` prop, the union of what the two copies pass).
 * What a caller does when it is NOT paging is the caller's: the chronology
 * renders nothing while collapsed, the Documents section renders every row
 * when `paginate` is off — both keep that gate at the call site, so both
 * paginate exactly as they did.
 */
import { useState } from "react";
import type { Dispatch, SetStateAction } from "react";

/** The app's page size: ten, the number the chronology and the Documents section both used. */
export const PAGE_SIZE = 10;

export interface PagerState {
  /** The requested page, 0-based, as the caller's `useState(0)` held it. */
  page: number;
  /** React's own setter type — a function type with a named parameter is a
   *  `no-unused-vars` warning here, and this file carries none. */
  setPage: Dispatch<SetStateAction<number>>;
}

export interface Page<T> {
  /** The rows of the current page. */
  pageItems: T[];
  /** 0-based current page, clamped into range. */
  cur: number;
  /** Page count, never below 1. */
  tot: number;
}

/** The state half: `useState(0)`, where the copies had it. */
export function usePager(): PagerState {
  const [page, setPage] = useState(0);
  return { page, setPage };
}

/** The arithmetic half, pure: the page count, the clamped page, its rows. */
export function pageOf<T>(items: readonly T[], page: number, pageSize: number = PAGE_SIZE): Page<T> {
  const tot = Math.max(1, Math.ceil(items.length / pageSize));
  const cur = Math.min(page, tot - 1);
  const pageItems = items.slice(cur * pageSize, (cur + 1) * pageSize);
  return { pageItems, cur, tot };
}
